import {client} from '@core/GraphQLProvider';
import {Catalog, CatalogItem, CatalogItemFullDetail, CatalogItemAccessories, BrandFamilies, ProductConfiguration} from './query.graphql';
import curry from 'ramda/src/curry';
import compose from 'ramda/src/compose';
import pCompose from 'ramda/src/composeP';
import prop from 'ramda/src/prop';
import uniq from 'ramda/src/uniq';
import map from 'ramda/src/map';
import sortBy from 'ramda/src/sortBy';
import identity from 'ramda/src/identity';

const DEFAULT_OPTIONS = {
    withAccessories: false,
    withPrice: false
};

const formatSku = id => /-/.test( id ) ? id : id + '-';

const buildCatalogItemQuery = curry(
    ( query, withOptions, skuValues ) => ({
        query,
        // todo -> create varBuilder type that can be unwrapped from here
        // so var creation isn't tightly coupled to this implementation
        fetchPolicy: 'no-cache',
        variables: {
            ...withOptions,
            skuValues: formatSku( skuValues )
        }
    })
);

const buildCatalogQuery = curry(
    ( query, withOptions = {} ) => ({
        query,
        fetchPolicy: 'cache-first',
        variables: {
            ...withOptions
        }
    })
);

const queryProp = propName => pCompose(
    prop( propName ), prop( 'data' ), client.query
);
const queryCatalogWithOptions = curry(
    compose( queryProp( 'allProducts' ), buildCatalogQuery )
);
const queryProductWithOptions = curry(
    compose( queryProp( 'product' ), buildCatalogItemQuery )
);

export default {
    getAll: () => queryCatalogWithOptions( Catalog ),

    getAllForSearch: () => queryCatalogWithOptions( Catalog, {withImages: true, withDocuments: true} ),

    getDetail:
        queryProductWithOptions( CatalogItem, DEFAULT_OPTIONS ),

    getDetailWithAccessories:
        queryProductWithOptions( CatalogItem, {withAccessories: true} ),

    getPurchaseDetail:
        queryProductWithOptions( CatalogItem, {withPrice: true} ),

    getFullDetail:
        queryProductWithOptions( CatalogItemFullDetail, DEFAULT_OPTIONS ),

    getFullDetailWithAccessories:
        queryProductWithOptions( CatalogItemFullDetail, {withAccessories: true} ),

    async getBrandFamilies () {
        const catalog = await queryCatalogWithOptions( Catalog, DEFAULT_OPTIONS );
        // map on "brandName then dedupe list
        return compose(
            sortBy( identity ),
            uniq,
            map( prop( 'brandName' ) )
        )( catalog );
    },

    async getAccessories ( id ) {
        const product = await queryProductWithOptions( CatalogItemAccessories, DEFAULT_OPTIONS, id );
        return product.accessories;
    },

    async getConfiguration ( brandId ) {
        const result = await client.query( {
            query: ProductConfiguration,
            fetchPolicy: 'no-cache',
            variables: { brandId }
        } );
        return result.data.productConfiguration;
    }
};
